import api from './api';
import { 
  TasksResponse, 
  TaskResponse, 
  TaskFormData, 
  TaskCategoriesResponse,
  TaskStatsResponse 
} from '../types/Task';

export const taskApi = {
  // Get all tasks with pagination and filters
  getTasks: async (
    page: number = 1, 
    limit: number = 20, 
    search: string = '',
    status: string = '',
    priority: string = '',
    categoryId: string = '',
    assigneeId: string = '',
    leadId: string = '',
    origin: string = ''
  ): Promise<TasksResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search }),
      ...(status && { status }),
      ...(priority && { priority }),
      ...(categoryId && { categoryId }),
      ...(assigneeId && { assigneeId }),
      ...(leadId && { leadId }),
      ...(origin && { origin })
    });

    const response = await api.get(`/tasks?${params}`);
    return response.data;
  },

  // Get task by ID
  getTaskById: async (id: number): Promise<TaskResponse> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },

  // Get the current user's tasks-module view permissions (my-tasks-only / view-all).
  // Routed through the shared api instance so it uses the same base URL, auth header
  // and 401 handling as every other call (no divergent hardcoded URL).
  getTasksViewPermissions: async (): Promise<{ success: boolean; viewMyTasksOnly: boolean; viewAllTasks: boolean }> => {
    const response = await api.get('/role-management/tasks-view-permissions');
    return response.data;
  },

  // Get tasks by lead ID
  getTasksByLeadId: async (leadId: number): Promise<TasksResponse> => {
    const response = await api.get(`/tasks/lead/${leadId}`);
    return response.data;
  },

  // Create new task
  createTask: async (taskData: TaskFormData): Promise<TaskResponse> => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },

  // Update task
  updateTask: async (id: number, taskData: TaskFormData): Promise<TaskResponse> => {
    const response = await api.put(`/tasks/${id}`, taskData);
    return response.data;
  },

  // Delete task
  deleteTask: async (id: number): Promise<TaskResponse> => {
    const response = await api.delete(`/tasks/${id}`);
    return response.data;
  },

  // Update task status
  updateTaskStatus: async (id: number, status: string): Promise<TaskResponse> => {
    const response = await api.patch(`/tasks/${id}/status`, { status });
    return response.data;
  },

  // Get task categories
  getTaskCategories: async (): Promise<TaskCategoriesResponse> => {
    const response = await api.get('/tasks/categories/list');
    return response.data;
  },

  // Search leads for task assignment
  searchLeads: async (search: string = ''): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get(`/tasks/leads/search?search=${encodeURIComponent(search)}`);
    return response.data;
  },

  // Get users for task assignment
  getUsers: async (): Promise<{ success: boolean; data: any[] }> => {
    const response = await api.get('/tasks/users/list');
    return response.data;
  },

  // Complete task with user-selected stage (for prompt mode automation)
  completeWithStageSelection: async (
    taskId: number, 
    selectionId: number, 
    selectedStageId: number
  ): Promise<{ success: boolean; message: string; data: any }> => {
    const response = await api.post(`/tasks/${taskId}/complete-with-stage`, {
      selectionId,
      selectedStageId
    });
    return response.data;
  },

  // Cancel pending stage selection
  cancelStageSelection: async (
    taskId: number, 
    selectionId: number
  ): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/tasks/${taskId}/cancel-stage-selection`, {
      selectionId
    });
    return response.data;
  },

  // Get pending stage selection for a task
  getPendingStageSelection: async (taskId: number): Promise<{ success: boolean; data: any }> => {
    const response = await api.get(`/tasks/${taskId}/pending-stage-selection`);
    return response.data;
  },

  // Get all pending stage selections for the organization
  getAllPendingStageSelections: async (): Promise<{ success: boolean; data: any[]; count: number }> => {
    const response = await api.get('/tasks/pending-stage-selections');
    return response.data;
  }
};
